import { Dialog } from "@headlessui/react";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import classNames from "classnames";
import { graphql, Link, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import React from "react";
import { MenuLink } from "../../../types/app";
import DisclosureNav from "../disclosure-nav";
import IconCaretDown from "../icons/caret-down";
import IconMenu from "../icons/menu";
import IconX from "../icons/x";

export interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className }) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);

  const handleMobileTriggerClick = React.useCallback(() => {
    setIsMobileNavOpen(!isMobileNavOpen);
  }, [isMobileNavOpen]);

  const {
    allMenuLinksJson: { edges: menuLinks },
  } = useStaticQuery<QueryType>(query);

  return (
    <div
      className={classNames(
        "w-full border-b border-b-red-accent/25 z-50",
        className
      )}
    >
      <div className="flex flex-row gap-3 justify-between items-center py-4 md:items-start md:flex-col lg:items-center lg:flex-row container-7xl">
        <Link to="/" className="flex gap-2 items-center flew-row shrink-0">
          <StaticImage
            src="../../assets/images/logo.gif"
            alt="Logo"
            className="w-12 h-12"
          />
          <span className="text-sm text-slate-700">
            Psychotherapie Praxis
            <br />
            Düsseldorf
          </span>
        </Link>
        <NavigationMenu.Root className="hidden relative z-10 justify-start w-full lg:justify-end md:flex">
          <NavigationMenu.List className="flex gap-x-1 lg:gap-x-1 xl:gap-x-2">
            {menuLinks.map((link) =>
              link.node.sublinks ? (
                <NavigationMenu.Item className="relative" key={link.node.link}>
                  <NavigationMenu.Trigger className="flex items-center gap-0.5 p-2 text-sm tracking-wide rounded group text-slate-700 hover:bg-red-accent hover:text-white data-[state=open]:bg-red-accent data-[state=open]:text-white data-[state=open]:rounded-b-none">
                    <span>{link.node.name}</span>
                    <IconCaretDown
                      className="relative w-2.5 h-2.5 transition-transform duration-[250] ease-in group-data-[state=open]:-rotate-180 opacity-50"
                      aria-hidden
                    />
                  </NavigationMenu.Trigger>
                  <NavigationMenu.Content className="absolute left-0 top-full w-full">
                    <ul className="flex z-50 flex-col gap-2 p-2 w-60 bg-white rounded-b-lg rounded-tr-lg border border-red-accent/25">
                      {link.node.sublinks!.map((child) => (
                        <li style={{ gridRow: "span 3" }} key={child.link}>
                          <NavigationMenu.Item>
                            <Link
                              to={child.link}
                              className="block p-2 text-sm rounded text-slate-700 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-100"
                              activeClassName="bg-red-accent text-white hover:!bg-red-accent hover:!text-white"
                              partiallyActive={false}
                            >
                              {child.name}
                            </Link>
                          </NavigationMenu.Item>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              ) : (
                <NavigationMenu.Item asChild key={link.node.link}>
                  <Link
                    key={link.node.link}
                    to={link.node.link}
                    className="block p-2 text-sm tracking-wide rounded text-slate-700 hover:bg-red-accent hover:text-white"
                    activeClassName="bg-red-accent text-white"
                  >
                    {link.node.name}
                  </Link>
                </NavigationMenu.Item>
              )
            )}
          </NavigationMenu.List>
        </NavigationMenu.Root>

        <button
          className="cursor-pointer md:hidden"
          onClick={handleMobileTriggerClick}
          aria-label="Mobile Menu"
        >
          <IconMenu className="w-6 h-6 text-red-accent" />
        </button>

        <Dialog
          open={isMobileNavOpen}
          onClose={() => setIsMobileNavOpen(false)}
          className="relative z-50"
        >
          <div className="overflow-y-auto fixed inset-0 bg-white">
            <div className="flex justify-center items-center">
              <Dialog.Panel className="mx-auto w-full h-full">
                <div className="flex flex-row justify-between px-4 py-4">
                  <Link to="/" className="flex gap-2 items-center flew-row">
                    <StaticImage
                      src="../../assets/images/logo.gif"
                      alt="Logo"
                      className="w-12 h-12"
                    />
                    <span className="text-sm text-slate-700">
                      Psychotherapie Praxis
                      <br />
                      Düsseldorf
                    </span>
                  </Link>
                  <button
                    className="cursor-pointer"
                    onClick={handleMobileTriggerClick}
                  >
                    <IconX className="w-6 h-6 text-red-accent" />
                  </button>
                </div>
                <hr className="border-t-red-accent/25" />
                <div className="flex flex-col gap-2 px-4 py-6">
                  {menuLinks.map((link) =>
                    link.node.sublinks ? (
                      <DisclosureNav
                        title={link.node.name}
                        key={link.node.link}
                      >
                        {link.node.sublinks.map((link2) => (
                          <Link
                            key={link2.link}
                            to={link2.link}
                            className="block p-2 text-sm font-light rounded text-red-accent decoration-red-accent/25 hover:decoration-red-accent"
                            activeClassName="bg-red-accent text-white"
                            onClick={handleMobileTriggerClick}
                          >
                            {link2.name}
                          </Link>
                        ))}
                      </DisclosureNav>
                    ) : (
                      <Link
                        key={link.node.link}
                        to={link.node.link}
                        className="block p-2 text-sm rounded text-slate-700"
                        activeClassName="bg-red-accent text-white"
                        onClick={handleMobileTriggerClick}
                      >
                        {link.node.name}
                      </Link>
                    )
                  )}
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

const query = graphql`
  query HeaderQuery {
    allMenuLinksJson {
      edges {
        node {
          name
          link
          sublinks {
            name
            link
          }
        }
      }
    }
  }
`;

type QueryType = {
  allMenuLinksJson: {
    edges: {
      node: MenuLink;
    }[];
  };
};

export default Header;
